import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface CatalogFeature {
  key: string;
  module: string;
  label: string;
  description: string;
}

/** Catalog feature annotated with its enabled state for a specific org. */
export interface OrgFeature extends CatalogFeature {
  enabled: boolean;
}

export interface FeatureUpdate {
  key: string;
  enabled: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

type Result<T> = { success: boolean; data?: T; message?: string; error?: { message: string } | null };

const SA_BASE = '/super-admin/org-features';

const ok = <T,>(data: T, extra: Partial<Result<T>> = {}): Result<T> => ({ success: true, data, error: null, ...extra });
const fail = <T,>(error: any): Result<T> => ({
  success: false,
  error: { message: error?.response?.data?.message ?? error?.message ?? 'Request failed' },
});

// ===== SUPER ADMIN MANAGEMENT =====

/** The full catalog of gateable functionalities. */
export const getCatalog = async (): Promise<Result<CatalogFeature[]>> => {
  try {
    const res = await httpClient.get<ApiResponse<CatalogFeature[]>>(`${SA_BASE}/catalog`);
    return ok(res.data || []);
  } catch (e) { return fail(e); }
};

/** One org's catalog annotated with each feature's enabled state. */
export const getOrgFeatures = async (organizationId: number): Promise<Result<OrgFeature[]>> => {
  try {
    const res = await httpClient.get<ApiResponse<OrgFeature[]>>(`${SA_BASE}/${organizationId}`);
    return ok(res.data || []);
  } catch (e) { return fail(e); }
};

/** Apply a batch of ON/OFF changes for an org. */
export const updateOrgFeatures = async (
  organizationId: number,
  features: FeatureUpdate[]
): Promise<Result<{ applied: number }>> => {
  try {
    const res = await httpClient.put<ApiResponse<{ applied: number }> & { applied?: number }>(
      `${SA_BASE}/${organizationId}`,
      { features }
    );
    return ok({ applied: (res as any).applied ?? 0 }, { message: res.message });
  } catch (e) { return fail(e); }
};

// ===== CONSUMER (any authenticated user) =====

/** The feature keys the calling user's own org is allowed to use. */
export const getMyFeatures = async (): Promise<Result<{ allowedFeatures: string[]; organizationId: number | null }>> => {
  try {
    const res = await httpClient.get<ApiResponse<{ allowedFeatures: string[]; organizationId: number | null }>>(
      '/org-features/me'
    );
    return ok(res.data);
  } catch (e) { return fail(e); }
};
